import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border-b border-slate-200 last:border-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 text-left group"
      aria-expanded={isOpen}
    >
      <span className="font-semibold text-secondary group-hover:text-primary transition-colors pr-4">
        {question}
      </span>
      <ChevronDown 
        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    <div 
      className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}
    >
      <p className="text-slate-600 leading-relaxed">{answer}</p>
    </div>
  </div>
);

const PracticeAreaFAQ = ({ 
  title = "Frequently Asked Questions",
  subtitle,
  faqs,
  accentColor = "primary", // primary (pink), ocean (teal), etc.
  schemaId = "practice-area-faq" // unique ID for schema script
}) => {
  const [openIndex, setOpenIndex] = useState(0);

  // Add FAQ Schema for SEO
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    // Remove existing schema with same ID
    const existingScript = document.getElementById(`faq-schema-${schemaId}`);
    if (existingScript) {
      existingScript.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `faq-schema-${schemaId}`;
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(`faq-schema-${schemaId}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [faqs, schemaId]);

  const colorClasses = {
    primary: {
      bg: 'bg-coral-50',
      icon: 'text-primary',
      iconBg: 'bg-coral-100'
    },
    rose: {
      bg: 'bg-coral-50',
      icon: 'text-coral-600',
      iconBg: 'bg-coral-100'
    },
    ocean: {
      bg: 'bg-ocean-50',
      icon: 'text-ocean-600',
      iconBg: 'bg-ocean-100'
    },
    teal: {
      bg: 'bg-teal-50',
      icon: 'text-teal-600',
      iconBg: 'bg-teal-100'
    }
  };

  // Default to primary (rose) for consistency
  const colors = colorClasses[accentColor] || colorClasses.primary;

  return (
    <section className={`py-16 ${colors.bg}`} data-testid="practice-area-faq">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 ${colors.iconBg} ${colors.icon} px-4 py-2 rounded-full text-sm font-medium mb-4`}>
            <HelpCircle className="w-4 h-4" />
            Common Questions
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
            {title}
          </h2>
          {subtitle && (
            <p className="text-slate-600 max-w-2xl mx-auto">{subtitle}</p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PracticeAreaFAQ;
